import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import RoleplayDebriefView from './RoleplayDebriefView';

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SITUATION_FIELDS = [
  { label: 'Counterpart',        getValue: cs => cs?.characterBrief?.inSum?.content },
  { label: 'Real Issue',  getValue: cs => cs?.realIssue?.summary?.content },
  { label: 'Data & Reasoning',   getValue: cs => cs?.realIssue?.dataAndReasoning?.content },
  { label: 'Stakes — Downsides', getValue: cs => cs?.characterStakes?.downSides?.inSum?.content },
  { label: 'Stakes — Upsides',   getValue: cs => cs?.characterStakes?.upSides?.inSum?.content },
  { label: 'Desired Outcome',    getValue: cs => cs?.desiredOutcome?.content },
  { label: 'Challenge',          getValue: cs => cs?.challenge?.content },
];

const SituationView = ({ caseStudy }) => {
  if (!caseStudy) return <p className="rp-script-empty">Situation not available.</p>;
  // console.log('[SituationView] caseStudy top-level keys:', Object.keys(caseStudy));
  // console.log('[SituationView] caseStudy full:', JSON.stringify(caseStudy, null, 2));
    return (
    <div className="rp-script-list">
      {SITUATION_FIELDS.map(({ label, getValue }) => {
        const value = getValue(caseStudy);
        if (!value) return null;
        return (
          <div key={label} className="rp-impl-cs-section">
            <div className="rp-impl-cs-label">{label}</div>
            <div className="rp-impl-cs-value">{value}</div>
          </div>
        );
      })}
    </div>
  );
};

SituationView.propTypes = { caseStudy: PropTypes.object };

const RoleplayScriptPanel = ({ exchanges, open, onToggle, characterName, caseStudy, debriefReport, onRetryDebriefBody, debriefActive, activeTab, onTabChange, onRequestOpen }) => {
  const goldBottomRef = useRef(null);
  const panelRef = useRef(null);
  const [width, setWidth] = useState(300);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);

    const onMouseMove = (ev) => {
      if (!panelRef.current) return;
      const right = panelRef.current.getBoundingClientRect().right;
      setWidth(Math.max(200, Math.min(600, right - ev.clientX)));
    };

    const onMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  useEffect(() => {
    if (debriefActive) {
      onTabChange('debrief');
      onRequestOpen();
    }
  }, [debriefActive, onTabChange, onRequestOpen]);

  useEffect(() => {
    if (activeTab === 'gold' && open) {
      goldBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [exchanges, activeTab, open]);

  return (
    <div
      ref={panelRef}
      className={`rp-script-panel ${open ? 'open' : 'closed'}${isDragging ? ' resizing' : ''}`}
      style={open ? { width, minWidth: width } : undefined}
    >
      {open && <div className="rp-script-resize-handle" onMouseDown={handleDragStart} />}
      <div className="rp-script-header">
        <div className="rp-script-tabs">
          {open && (
            <>
              <button
                className={`rp-script-tab ${activeTab === 'situation' ? 'active' : ''}`}
                onClick={() => onTabChange('situation')}
              >
                Situation
              </button>
              <button
                className={`rp-script-tab ${activeTab === 'gold' ? 'active' : ''}`}
                onClick={() => onTabChange('gold')}
              >
                Gold Version
              </button>
              {debriefActive && (
                <button
                  className={`rp-script-tab ${activeTab === 'debrief' ? 'active' : ''}`}
                  onClick={() => onTabChange('debrief')}
                >
                  Debrief
                </button>
              )}
            </>
          )}
        </div>
        <button
          className="rp-script-toggle-inline"
          onClick={onToggle}
          title={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronRight /> : <ChevronLeft />}
        </button>
      </div>

      {open && (
        <>
          {activeTab === 'situation' && (
            <SituationView caseStudy={caseStudy} />
          )}

          {activeTab === 'gold' && (
            <>
              <div className="rp-script-list">
                {exchanges.length === 0 ? (
                  <p className="rp-script-empty">Promoted exchanges will appear here.</p>
                ) : (
                  exchanges.map((ex, i) => (
                    <div
                      key={i}
                      className={`rp-script-exchange${i === exchanges.length - 1 ? ' is-new' : ''}`}
                    >
                      {ex.characterText && (
                        <>
                          <div className="rp-script-speaker-label them">{characterName || 'them'}</div>
                          <p className="rp-script-text character">{ex.characterText}</p>
                        </>
                      )}
                      {ex.clientText && (
                        <>
                          <div className="rp-script-speaker-label you">You</div>
                          <p className="rp-script-text">{ex.clientText}</p>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div ref={goldBottomRef} />
              <div className="rp-script-footer">
                {exchanges.length} {exchanges.length === 1 ? 'exchange' : 'exchanges'} captured
              </div>
            </>
          )}

          {activeTab === 'debrief' && (
            <div className="rp-script-debrief-tab">
              <RoleplayDebriefView report={debriefReport} onRetryBody={onRetryDebriefBody} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

RoleplayScriptPanel.propTypes = {
  exchanges: PropTypes.arrayOf(
    PropTypes.shape({
      clientText: PropTypes.string,
      characterText: PropTypes.string,
    })
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  characterName: PropTypes.string,
  caseStudy: PropTypes.object,
  debriefReport: PropTypes.object,
  onRetryDebriefBody: PropTypes.func,
  debriefActive: PropTypes.bool,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onRequestOpen: PropTypes.func.isRequired,
};

export default RoleplayScriptPanel;
